import { useState, useEffect, useCallback, useRef } from 'react';
import { getUnreadCountsByStudent } from '../utils/notificationCountsService';

/**
 * Teacher-facing unread badge counts (submitted homework + student chat
 * messages), per student and totalled. Self-contained — fetches on mount
 * and refreshes on the same "soft realtime" window events the rest of the
 * app already uses (homeworkUpdated, chatMessageSent, chatRead) plus tab
 * visibility, rather than opening a realtime channel per student.
 */
export function useTeacherUnreadCounts() {
  const [byStudent, setByStudent] = useState({});
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const counts = await getUnreadCountsByStudent();
      if (mountedRef.current) setByStudent(counts);
    } catch (e) {
      console.error('useTeacherUnreadCounts refresh failed:', e?.message);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    const onHomeworkUpdated = () => refresh();
    const onChatMessageSent = () => refresh();
    const onChatRead = () => refresh();
    const onVisibility = () => { if (document.visibilityState === 'visible') refresh(); };
    window.addEventListener('homeworkUpdated', onHomeworkUpdated);
    window.addEventListener('chatMessageSent', onChatMessageSent);
    window.addEventListener('chatRead', onChatRead);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      mountedRef.current = false;
      window.removeEventListener('homeworkUpdated', onHomeworkUpdated);
      window.removeEventListener('chatMessageSent', onChatMessageSent);
      window.removeEventListener('chatRead', onChatRead);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refresh]);

  const totalHomework = Object.values(byStudent).reduce((sum, c) => sum + (c.homework || 0), 0);
  const totalChat = Object.values(byStudent).reduce((sum, c) => sum + (c.chat || 0), 0);

  return { byStudent, totalHomework, totalChat, total: totalHomework + totalChat, refresh };
}

export default useTeacherUnreadCounts;
